import { Container, Graphics } from 'pixi.js';
import { VISUALS } from '../config.ts';
import type { LevelDefinition } from '../levels/Level.ts';
import type { Collider } from '../simulation/Collision.ts';

/**
 * Static level geometry plus the two fixtures: the fluid source and the goal.
 *
 * Seen from above, walls are objects standing on the table rather than lines drawn on it.
 * What sells that is not height — under an orthographic top-down camera there is no
 * height to see — it is that the top face is lit *across* its short axis, bright on the
 * lamp side and falling away to a dark cheek on the far side. A bar lit evenly reads as a
 * stroke however many outlines it is given.
 *
 * Graphics has no gradient fill, so the grade is built from a stack of capsules, each one
 * inset a little further and shifted the same distance towards the lamp. That keeps every
 * band's lamp-side edge in exactly the same place while its far edge marches inward, so
 * each successive fill leaves a crescent of the one below showing on the shadow side. Five
 * bands over the thirteen device pixels of a 26wu bar is about the point where adding more
 * stops being visible.
 *
 * Two shadows, not one: a cast shadow offset away from the lamp, and a tight contact
 * shadow with no offset at all. The second is the one that welds a bar to the table —
 * without it the cast shadow alone reads as a bar floating above the board.
 *
 * Walls are rebuilt once per level. The goal animates, so it lives in its own Graphics.
 */

// Solved through the post chain, not picked by eye. The board's brightest scenery has to
// stay under the contour lines, which measure about L 0.48 on screen: the lines are
// hairlines and the walls are the largest shapes on the board, so equal luminance is not
// equal weight. The lip lands near 0.36 and the cheek near 0.13, which gives one bar an
// internal range wider than the gap between the old flat wall and the table.

/** The side of the bar, and what shows through the contact shadow. */
const WALL_BODY = 0x0f131c;
/** The top face, away from the lamp. */
const WALL_CHEEK = 0x171d29;
/** The top face, at the lamp-facing edge. */
const WALL_LIP = 0x39414f;

/** How far inside the bar's outline the top face starts, in world units. */
const WALL_TOP_INSET = 5;
/** Bands in the cross-axis grade. Below about seven the steps are visible as banding. */
const WALL_BANDS = 5;

/** Linear blend between two packed 0xRRGGBB colours. */
function mixColor(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 255;
  const ag = (a >> 8) & 255;
  const ab = a & 255;
  const br = (b >> 16) & 255;
  const bg = (b >> 8) & 255;
  const bb = b & 255;
  return (
    ((Math.round(ar + (br - ar) * t) << 16) |
      (Math.round(ag + (bg - ag) * t) << 8) |
      Math.round(ab + (bb - ab) * t)) >>>
    0
  );
}

/** Half the bar's short axis — how much room the grade has to work in. */
function colliderHalfExtent(collider: Collider): number {
  switch (collider.type) {
    case 'segment':
      return collider.thickness / 2;
    case 'circle':
      return collider.r;
    case 'rect':
      return Math.min(collider.w, collider.h) / 2;
  }
}
const GOAL_COLOR = 0x3ad46f;
/** The lit far wall of the goal's well. Faintly green, so the socket belongs to the goal. */
const WELL_LIT = 0x131f18;
/** The bottom of it. */
const WELL_DEEP = 0x05080d;
/**
 * Bands in the well's grade.
 *
 * More than the walls need — the goal is a hundred device pixels across where a bar is
 * twenty — but every band is a full-size disc drawn over the last, so this is also the
 * most expensive overdraw on the board. Twelve is where the rings stop being visible and
 * the cost stops being worth arguing about. Baking it to a texture was tried and came out
 * slower: under a software rasteriser the render-target switch costs more than the fills.
 */
const WELL_BANDS = 12;
/** Radius of the progress gauge, as a multiple of the goal radius. */
const GOAL_TRACK = 1.16;
const SOURCE_COLOR = 0x8fa2c8;

export class LevelRenderer {
  readonly container = new Container({ label: 'level' });

  private readonly shadows = new Graphics();
  private readonly bodies = new Graphics();
  private readonly tops = new Graphics();
  private readonly source = new Graphics();
  /**
   * The parts of the goal that never move: the well, its bezel, the progress track.
   *
   * Split out from the animated half deliberately. Graphics re-tessellates everything it
   * holds on clear(), so leaving eighteen well bands in the per-frame object cost about a
   * third of the frame — measured 16.3ms to 21.4ms — to redraw geometry that is identical
   * every time. Only the sweep, the ticks and the glow actually change.
   */
  private readonly goalWell = new Graphics();
  private readonly goal = new Graphics();
  /**
   * Additive, so the goal has a light of its own.
   *
   * The post chain's bloom exists for exactly this, and the goal was the one object on
   * the board it never reached: a flat stroke peaked below the threshold, so the only
   * things that glowed were the fluid and the magnets — the post was reinforcing
   * everything except the thing the player is trying to reach.
   */
  private readonly goalGlow = new Graphics();

  private goalRadius = 0;

  constructor() {
    // Goal above the wall shadows but below the wall bodies, so it reads as painted on
    // the table rather than floating over it.
    this.goalGlow.blendMode = 'add';
    this.container.addChild(
      this.shadows,
      this.goalWell,
      this.goal,
      this.goalGlow,
      this.bodies,
      this.tops,
      this.source,
    );
  }

  build(level: LevelDefinition): void {
    this.goalRadius = level.goal.radius;
    this.drawWalls(level.walls);
    this.drawSource(level);
    this.goal.position.set(level.goal.x, level.goal.y);
    this.goalWell.position.set(level.goal.x, level.goal.y);
    this.goalGlow.position.set(level.goal.x, level.goal.y);
    this.drawGoalWell();
  }

  private drawWalls(colliders: readonly Collider[]): void {
    const { x: lightX, y: lightY } = VISUALS.lightDirection;
    const castX = -lightX * VISUALS.shadowOffset;
    const castY = -lightY * VISUALS.shadowOffset;

    this.shadows.clear();
    this.bodies.clear();
    this.tops.clear();

    for (const collider of colliders) {
      // Cast shadow, thrown away from the lamp.
      traceCollider(this.shadows, collider, castX, castY, 0);
      this.shadows.fill({ color: 0x000000, alpha: 0.30 });

      // Contact shadow: no offset, a little larger than the bar, so it survives as a tight
      // dark halo all the way round once the body is drawn over it. Occlusion, not a cast
      // — it is what stops the bar reading as pasted onto the board.
      traceCollider(this.shadows, collider, 0, 0, -5);
      this.shadows.fill({ color: 0x000000, alpha: 0.34 });

      traceCollider(this.bodies, collider, 0, 0, 0);
      this.bodies.fill(WALL_BODY);

      // The cross-axis grade. See the note at the top of the file for why this is a stack
      // of offset capsules rather than a gradient.
      const half = colliderHalfExtent(collider);
      const depth = Math.max(half - WALL_TOP_INSET - 1, 0);
      const step = depth / WALL_BANDS;
      for (let i = 0; i < WALL_BANDS; i++) {
        const shift = i * step;
        traceCollider(
          this.tops,
          collider,
          lightX * shift,
          lightY * shift,
          WALL_TOP_INSET + shift,
        );
        this.tops.fill(mixColor(WALL_CHEEK, WALL_LIP, i / (WALL_BANDS - 1)));
      }
    }
  }

  private drawSource(level: LevelDefinition): void {
    const { source, radius } = level.fluid;
    const graphics = this.source;
    graphics.clear();

    const offsetX = -VISUALS.lightDirection.x * VISUALS.shadowOffset * 0.6;
    const offsetY = -VISUALS.lightDirection.y * VISUALS.shadowOffset * 0.6;

    graphics
      .circle(source.x + offsetX, source.y + offsetY, radius + 14)
      .fill({ color: 0x000000, alpha: 0.35 })
      // Housing.
      .circle(source.x, source.y, radius + 14)
      .fill({ color: 0x1a2233 })
      .stroke({ width: 2, color: SOURCE_COLOR, alpha: 0.45 })
      // The mouth: a hole in the table, so it goes darker rather than lighter.
      .circle(source.x, source.y, radius)
      .fill({ color: 0x05070d })
      .stroke({ width: 2, color: SOURCE_COLOR, alpha: 0.7 });

    // Four alignment notches around the rim — free detail that sells the scale.
    for (let i = 0; i < 4; i++) {
      const angle = (Math.PI / 2) * i + Math.PI / 4;
      const inner = radius + 3;
      const outer = radius + 12;
      graphics
        .moveTo(source.x + Math.cos(angle) * inner, source.y + Math.sin(angle) * inner)
        .lineTo(source.x + Math.cos(angle) * outer, source.y + Math.sin(angle) * outer)
        .stroke({ width: 3, color: SOURCE_COLOR, alpha: 0.5, cap: 'round' });
    }
  }

  /**
   * The static half of the goal: a socket sunk into the table, not a target painted on it.
   *
   * The well uses the same stack of offset shapes the walls use, ramped the other way. A
   * pit is lit on its *far* inner wall while a bump is lit on its near one, and that
   * inversion is the whole reason one reads as a hole and the other as something standing
   * on the board. So the discs march towards the lamp and darken as they go, leaving the
   * brightest crescent on the side the light falls into.
   *
   * This used to be a flat translucent disc, on the grounds that a dark well would read as
   * a smudge over a saturated field. That was true when the table was clipped to black and
   * the wash was the only thing in frame; with a real slate underneath and the wash at a
   * third of its old gain, depth reads.
   */
  private drawGoalWell(): void {
    const graphics = this.goalWell;
    graphics.clear();

    const radius = this.goalRadius;
    const { x: lightX, y: lightY } = VISUALS.lightDirection;

    for (let i = 0; i < WELL_BANDS; i++) {
      const t = i / (WELL_BANDS - 1);
      // Shift and shrink by the same amount, so every band's lamp-side edge lands in
      // exactly the same place and only its far edge moves. Offsetting less than you
      // shrink — which is the easy mistake — gives concentric rings instead of a
      // direction, and the well reads as a target again.
      const shift = t * radius * 0.42;
      graphics
        .circle(lightX * shift, lightY * shift, radius * (1 - t * 0.42))
        .fill(mixColor(WELL_LIT, WELL_DEEP, t));
    }

    // The bezel, machined from the same metal as the walls so the goal belongs to the
    // board rather than being annotated onto it. A closed ring cannot be lit from one
    // side, so the highlight is an explicit arc across the lamp-facing quadrant.
    graphics.circle(0, 0, radius).stroke({ width: 5, color: WALL_CHEEK, alpha: 0.95 });
    const lampAngle = Math.atan2(lightY, lightX);
    // moveTo first: arc() joins from wherever the path currently is, which otherwise
    // draws a chord straight across the well.
    graphics
      .moveTo(Math.cos(lampAngle - 1.15) * radius, Math.sin(lampAngle - 1.15) * radius)
      .arc(0, 0, radius, lampAngle - 1.15, lampAngle + 1.15)
      .stroke({ width: 4, color: WALL_LIP, alpha: 0.9, cap: 'round' });

    // The empty gauge the sweep fills, on a dark backing.
    //
    // The backing is the fix for a measured failure: at alpha 0.14 the track was mostly
    // whatever lay behind it, so with a field lit the gauge went from rgb(35,92,81) —
    // unmistakably the game's green — to rgb(100,103,100), a neutral grey. The objective
    // stopped being identifiable at exactly the moment the player was planning a route to
    // it. Backing it means the green is the green whatever the field is doing, which is
    // the same trick the magnet already uses for its own ring.
    graphics
      .circle(0, 0, radius * GOAL_TRACK)
      .stroke({ width: 9, color: 0x060a10, alpha: 0.55 })
      .circle(0, 0, radius * GOAL_TRACK)
      .stroke({ width: 6, color: GOAL_COLOR, alpha: 0.42 });

  }

  /** `progress` is the goal dwell, 0..1; `elapsed` drives the idle pulse. */
  update(elapsed: number, progress: number, solved: boolean): void {
    const graphics = this.goal;
    const glow = this.goalGlow;
    graphics.clear();
    glow.clear();

    const radius = this.goalRadius;
    const track = radius * GOAL_TRACK;
    const breathe = 0.5 + 0.5 * Math.sin(elapsed * 1.9);

    // Swept at a radius outside the bezel, because the optimal play on most levels is an
    // attract magnet parked on the goal — so the winning move was also the move that
    // crowded the win condition.
    if (progress > 0.001) {
      graphics
        .moveTo(0, -track)
        .arc(0, 0, track, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * progress)
        .stroke({ width: 6, color: GOAL_COLOR, alpha: 0.95, cap: 'round' });
    }

    // Crosshair ticks, inside the sweep and out of its way.
    for (let i = 0; i < 4; i++) {
      const angle = (Math.PI / 2) * i;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      graphics
        .moveTo(cos * radius * 0.72, sin * radius * 0.72)
        .lineTo(cos * radius * 0.88, sin * radius * 0.88)
        .stroke({ width: 3, color: GOAL_COLOR, alpha: 0.22 + progress * 0.5, cap: 'round' });
    }

    // Solved, put into geometry and colour rather than alpha. The old flag moved a stroke
    // that was already at alpha 0.97 up to 1.0 — a 1.4% change, on the single frame the
    // whole level has been building towards.
    if (solved) {
      graphics.circle(0, 0, radius).stroke({ width: 9, color: 0xbdffd6, alpha: 1 });
      graphics.circle(0, 0, radius * 1.34).stroke({ width: 3, color: GOAL_COLOR, alpha: 0.55 });
      glow.circle(0, 0, radius * 0.96).fill({ color: GOAL_COLOR, alpha: 0.20 });
    }

    // The additive pass. Small numbers: this sums on top of everything below it, and the
    // scene buffer is half-float, so it climbs past the bloom threshold on its own.
    const lit = 0.05 + progress * 0.30 + breathe * 0.03 + (solved ? 0.34 : 0);
    glow.circle(0, 0, track).stroke({ width: 5, color: GOAL_COLOR, alpha: lit });
  }
}

/**
 * Lay down one collider's outline, optionally offset and shrunk.
 *
 * Shrinking is how the lit top face is produced: the same shape, a few units smaller, so
 * a rim of the darker body stays visible all the way round.
 */
function traceCollider(
  graphics: Graphics,
  collider: Collider,
  offsetX: number,
  offsetY: number,
  shrink: number,
): void {
  switch (collider.type) {
    case 'segment': {
      const half = Math.max(collider.thickness / 2 - shrink, 1);
      const dx = collider.x2 - collider.x1;
      const dy = collider.y2 - collider.y1;
      const length = Math.hypot(dx, dy);
      if (length < 1e-6) {
        graphics.circle(collider.x1 + offsetX, collider.y1 + offsetY, half);
        return;
      }
      // Drawn as an explicit capsule rather than a stroke, so one path can be filled —
      // strokes cannot be shrunk and offset independently of their geometry.
      const ux = dx / length;
      const uy = dy / length;
      const nx = -uy * half;
      const ny = ux * half;
      const x1 = collider.x1 + offsetX;
      const y1 = collider.y1 + offsetY;
      const x2 = collider.x2 + offsetX;
      const y2 = collider.y2 + offsetY;
      const start = Math.atan2(ny, nx);

      graphics
        .moveTo(x1 + nx, y1 + ny)
        .lineTo(x2 + nx, y2 + ny)
        .arc(x2, y2, half, start, start - Math.PI, true)
        .lineTo(x1 - nx, y1 - ny)
        .arc(x1, y1, half, start + Math.PI, start, true)
        .closePath();
      break;
    }
    case 'circle':
      graphics.circle(collider.x + offsetX, collider.y + offsetY, Math.max(collider.r - shrink, 1));
      break;
    case 'rect': {
      const angle = collider.angle ?? 0;
      const halfWidth = Math.max(collider.w / 2 - shrink, 1);
      const halfHeight = Math.max(collider.h / 2 - shrink, 1);
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const corners: Array<[number, number]> = [
        [-halfWidth, -halfHeight],
        [halfWidth, -halfHeight],
        [halfWidth, halfHeight],
        [-halfWidth, halfHeight],
      ];
      const points = corners.map(([x, y]): [number, number] => [
        collider.x + offsetX + x * cos - y * sin,
        collider.y + offsetY + x * sin + y * cos,
      ]);
      graphics.moveTo(points[0][0], points[0][1]);
      for (let i = 1; i < points.length; i++) graphics.lineTo(points[i][0], points[i][1]);
      graphics.closePath();
      break;
    }
  }
}
